import { Violation } from 'src/types/ViewModel/Stowage/Violation/Violation';
import { StowageRuleContext } from 'src/types/ViewModel/Stowage/StowageRule/StowageRuleContext';
import { getShortFormattedCargoNumber, isManualCargo } from '../cargoOperationUtils';
import { getVoyageNumberString } from '../stowageRotationUtils';

/**
 * Generates cargo information text in the format: "cargo #123 at V1234 Port Name"
 */
const formatCargoInfo = (cargoNumber: number, portCall: any, isManual: boolean): string => {
    const shortFormattedCargoNumber = getShortFormattedCargoNumber(cargoNumber, isManual);
    const voyageNumberString = getVoyageNumberString(portCall);
    const portDisplayName = portCall.portDisplayName;

    return `cargo #${shortFormattedCargoNumber} at ${voyageNumberString} ${portDisplayName}`;
};

/**
 * Formats the final illegal commingling text with the consequence parameter and cargo information
 */
const formatIllegalComminglingText = (consequenceParameter: string, cargoInfoList: string[]): string => {
    if (cargoInfoList.length === 0) {
        return consequenceParameter;
    }

    const cargoInfoText =
        cargoInfoList.length === 1 ? cargoInfoList[0] : cargoInfoList.slice(0, -1).join(', ') + ' and ' + cargoInfoList[cargoInfoList.length - 1];

    return `${consequenceParameter}: Check ${cargoInfoText}`;
};

/**
 * Generates illegal commingling text for a single violation.
 * Used in stowage plan rules manager.
 */
export const getIllegalComminglingTextForViolation = (consequenceParameter: string, violation: Violation): string => {
    if (!violation.cargo || !violation.portCall || !violation.cargo?.cargoNumber) {
        return consequenceParameter;
    }

    const cargoInfo = formatCargoInfo(violation.cargo.cargoNumber, violation.portCall, isManualCargo(violation.cargo));
    return formatIllegalComminglingText(consequenceParameter, [cargoInfo]);
};

/**
 * Generates illegal commingling text for multiple tank contents from a stowage rule context.
 * Used in violations utils for override violation descriptions.
 */
export const getIllegalComminglingTextForStowageRuleContext = (consequenceParameter: string, stowageRuleContext: StowageRuleContext): string => {
    const tankContents = stowageRuleContext.tankContent?.cargoContents.filter((cc) => cc.cargo.cargoGuid !== stowageRuleContext.cargo?.cargoGuid);
    if (!tankContents?.length) {
        return consequenceParameter;
    }

    const cargoInfoList: string[] = [];

    tankContents.forEach((tankContent) => {
        const cargo = tankContent.stowage.cargoOperation?.cargo;
        const portCall = tankContent.stowage.cargoOperation?.berthCall?.portCall;

        // TODO: NULL-SAFETY - Null safety issue - cargo.cargoNumber could be 0 which is falsy but valid. Should check for null/undefined specifically: cargo.cargoNumber != null
        if (cargo && portCall && cargo.cargoNumber) {
            const cargoInfo = formatCargoInfo(cargo.cargoNumber, portCall, isManualCargo(cargo));
            cargoInfoList.push(cargoInfo);
        }
    });

    return formatIllegalComminglingText(consequenceParameter, cargoInfoList);
};
